import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import LanguageSelect from "../components/LanguageSelect";
import { signupAPI } from "../services/api";

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user, loading: verifying } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [signupOpen, setSignupOpen] = useState(false);

  // The link only appears while the operator has signup open
  useEffect(() => {
    signupAPI
      .config()
      .then(({ data }) => setSignupOpen(Boolean(data.enabled)))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(identifier, password);

    if (result.success) {
      navigate("/admin");
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (!verifying && user) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex justify-end -mt-2 mb-2">
          <LanguageSelect />
        </div>

        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
            <span className="text-5xl">💨</span>
          </div>
          {/* Generic on purpose: at this point nobody has said which shop
              they are, so naming one would be a guess */}
          <h1 className="text-3xl font-bold text-gray-800">
            {t("auth.login.title")}
          </h1>
          <p className="text-gray-600 mt-2">{t("auth.login.subtitle")}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="login-1" className="block text-sm font-medium text-gray-700 mb-2">
              {t("auth.login.identifier")}
            </label>
            <input id="login-1"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="login-2" className="block text-sm font-medium text-gray-700 mb-2">
              {t("auth.login.password")}
            </label>
            <input id="login-2"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
          >
            {loading ? t("auth.login.submitting") : t("auth.login.submit")}
          </button>

          <p className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:underline">
              {t("auth.forgot.link")}
            </Link>
          </p>
        </form>

        {signupOpen && (
          <p className="mt-6 text-center text-sm">
            <Link to="/signup" className="font-semibold text-primary-600 hover:underline">
              {t("signup.link")}
            </Link>
          </p>
        )}

        <p className="mt-8 text-center text-xs text-gray-500 space-x-3 rtl:space-x-reverse">
          <Link to="/terms" className="hover:underline">
            {t("auth.legal.termsLink")}
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:underline">
            {t("auth.legal.privacyLink")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
